// import { mockDeals } from "./__mocks__/deals";

import { AFFILIATE_DEALS_PROXY_URL } from '../config';
import { withTokensIfNeeded } from '../utils/withTokensIfNeeded';
import { getLinkIdFromAffiliateUrl } from './getLinkIdFromAffiliateUrl';
import { mapDealsToUIObjects } from './maps';
import { DealAPIData, DealAPIResponse, DealUIObject } from './types';
import { affiliateDealsActiveDateFilter } from './affiliateDealsActiveDateFilter';
import { getCurrency } from './getCurrency';

export async function getDealsFromAffiliateUrl(
    affiliateUrl: string,
    token?: string
): Promise<DealUIObject[]> {
    const linkId = getLinkIdFromAffiliateUrl(affiliateUrl);

    const deals = await getDealsByLinkId(linkId);

    deals.sort((a: DealUIObject, b: DealUIObject) => b.sortScore - a.sortScore);

    if (!token) {
        return deals;
    }

    return deals.map((deal) => {
        return {
            ...deal,
            url:
                typeof deal.url === 'string'
                    ? deal.url.replace('{{token}}', token).replace('{{web}}', 'true')
                    : deal.url,
        };
    });
}

export async function getDealsByLinkId(linkId: string): Promise<DealUIObject[]> {
    const response = await withTokensIfNeeded<DealAPIResponse>(
        `${AFFILIATE_DEALS_PROXY_URL}&currency=${getCurrency()}&filters[link_id]=${linkId}&${affiliateDealsActiveDateFilter()}`,
        {
            method: 'GET',
            mode: 'cors',
        }
    );

    const deals = response.data?.data || [];
    return mapDealsToUIObjects(deals);
}

const ALL_DEALS_PAGE_LIMIT = 100;
/** Fetches this many pages in parallel after the first page (cuts sequential round-trips). */
const ALL_DEALS_PARALLEL_PAGES = 6;
const TOP_DEALS_MAX_PAGES = 3;

async function fetchAllDealsPage(offset: number): Promise<DealAPIData[]> {
    const response = await withTokensIfNeeded<DealAPIResponse>(
        `${AFFILIATE_DEALS_PROXY_URL}&currency=${getCurrency()}&page[offset]=${offset}&page[limit]=${ALL_DEALS_PAGE_LIMIT}&order[rank]=DESC&${affiliateDealsActiveDateFilter()}`,
        {
            headers: {
                ContentType: 'application/json',
            },
        }
    );
    return response.data?.data ?? [];
}

/** Appends remaining pages (offset >= ALL_DEALS_PAGE_LIMIT) using batched parallel requests. */
async function fetchAllDealsRemainingPages(): Promise<DealAPIData[]> {
    const out: DealAPIData[] = [];
    let offset = ALL_DEALS_PAGE_LIMIT;
    let done = false;
    while (!done) {
        const offsets = Array.from(
            { length: ALL_DEALS_PARALLEL_PAGES },
            (_, i) => offset + i * ALL_DEALS_PAGE_LIMIT
        );
        const pages = await Promise.all(offsets.map((o) => fetchAllDealsPage(o)));
        for (const page of pages) {
            out.push(...page);
            if (page.length < ALL_DEALS_PAGE_LIMIT) {
                done = true;
                break;
            }
        }
        if (!done) {
            offset += ALL_DEALS_PARALLEL_PAGES * ALL_DEALS_PAGE_LIMIT;
        }
    }
    return out;
}

export const getTopDealsForScreen = async (
    maxPerBrandCount: number,
    maxResults: number
): Promise<DealUIObject[]> => {
    if (maxResults <= 0) {
        return [];
    }

    const counts: Record<string, number> = {};
    const seenDealIds = new Set<number>();
    const deals: DealUIObject[] = [];

    try {
        for (let pageIndex = 0; pageIndex < TOP_DEALS_MAX_PAGES; pageIndex += 1) {
            const page = await fetchAllDealsPage(pageIndex * ALL_DEALS_PAGE_LIMIT);

            for (const rawDeal of page) {
                const brandId = rawDeal.attributes.brand_id;
                const timesSeen = counts[brandId] || 0;

                if (maxPerBrandCount > 0 && timesSeen >= maxPerBrandCount) {
                    continue;
                }

                if (maxPerBrandCount > 0) {
                    counts[brandId] = timesSeen + 1;
                }

                const [deal] = mapDealsToUIObjects([rawDeal]);
                if (deal && !seenDealIds.has(deal.id)) {
                    seenDealIds.add(deal.id);
                    deals.push(deal);
                }

                if (deals.length >= maxResults) {
                    return deals;
                }
            }

            if (page.length < ALL_DEALS_PAGE_LIMIT) {
                break;
            }
        }
    } catch (error) {
        throw new Error('Failed to fetch top deals');
    }

    return deals;
};

export const getAllDeals = async (maxPerBrandCount: number): Promise<DealUIObject[]> => {
    let rawDeals: DealAPIResponse['data'] = [];
    const counts: Record<string, number> = {};

    try {
        const firstPage = await fetchAllDealsPage(0);
        rawDeals =
            firstPage.length < ALL_DEALS_PAGE_LIMIT
                ? firstPage
                : [...firstPage, ...(await fetchAllDealsRemainingPages())];
    } catch (error) {
        throw new Error('Failed to fetch all deals');
    }

    const cache = {} as Record<number, DealUIObject>;
    const deals =
        maxPerBrandCount > 0
            ? rawDeals.filter((rawDeal) => {
                  const timesSeen = counts[rawDeal.attributes.brand_id] || 0;
                  if (timesSeen >= maxPerBrandCount) {
                      return false;
                  }
                  counts[rawDeal.attributes.brand_id] = timesSeen + 1;
                  return true;
              })
            : rawDeals;
    const dealsToReturn = mapDealsToUIObjects(deals);
    dealsToReturn.forEach((element: DealUIObject) => {
        if (!cache[element.id]) {
            cache[element.id] = element;
        }
    });
    return Object.values(cache);
};
